import { Announcement03, DotsVertical, LinkExternal01, Signal01, Trash01, VideoRecorder } from "@untitledui/icons";
import { useState } from "react";
import {
  useCreatorCampaigns,
  useRemoveCreatorFromAllCampaigns,
  useRemoveCreatorFromCampaign,
} from "@/api/creator-campaigns";
import { useTikTokAdsAuthUrl, useTikTokAdsConnectionStatus } from "@/api/tiktok-ads";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { success } from "@/providers/ToastProvider";

const buildTikTokCampaignUrl = (advertiserId: string, campaignId: string) =>
  `https://ads.tiktok.com/i18n/perf/campaign?aadvid=${advertiserId}&keyword=${campaignId}`;

const buildVideosInCampaignForCreatorUrl = (
  campaignId: string,
  campaignName: string,
  creatorId: string,
  creatorName: string,
) =>
  `/video-explorer?campaign=${encodeURIComponent(JSON.stringify([{ id: campaignId, label: campaignName }]))}&creator=${encodeURIComponent(JSON.stringify([{ id: creatorId, label: creatorName }]))}`;

const buildAllCampaignsForCreatorUrl = (
  campaigns: { campaignId: string; campaignName: string }[],
  creatorId: string,
  creatorName: string,
) =>
  `/video-explorer?campaign=${encodeURIComponent(JSON.stringify(campaigns.map((c) => ({ id: c.campaignId, label: c.campaignName }))))}&creator=${encodeURIComponent(JSON.stringify([{ id: creatorId, label: creatorName }]))}`;

type ConfirmAction =
  | { type: "remove-single"; campaignId: string; campaignName: string | null }
  | { type: "remove-all" };

const NotConnectedState = () => {
  const { data: authData } = useTikTokAdsAuthUrl();

  return (
    <div className="space-y-4">
      <div className="inline-flex justify-start items-center gap-1.5">
        <Announcement03 className="w-5 h-5 text-tertiary" strokeWidth={2} />
        <div className="text-tertiary text-sm font-medium leading-tight">Ad's summary</div>
      </div>
      <div className="relative rounded-lg border border-border-primary pb-16">
        <div className="flex opacity-30 pointer-events-none divide-x divide-border-primary">
          <div className="flex-1 text-center px-4 py-6">
            <div className="text-base font-medium text-primary">0</div>
            <div className="text-sm text-tertiary">Active Campaigns</div>
          </div>
        </div>
        <div
          className="absolute inset-0
       bg-[linear-gradient(180deg,rgba(79,69,109,0.90)_48.16%,rgba(32,24,54,0.90)_90%)]
       backdrop-blur-[3px] flex items-center justify-between px-8 rounded-sm min-h-50"
        >
          <div className="flex flex-col gap-1 max-w-[260px]">
            <p className="text-white text-sm font-medium">See this creator's ad campaigns</p>
            <p className="text-white/70 text-xs">Pull in the campaigns this creator's videos are running in.</p>
          </div>
          <a
            href={authData?.authUrl ?? "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 transition-colors"
          >
            <LinkExternal01 className="w-4 h-4" />
            Connect TikTok Ads
          </a>
        </div>
      </div>
    </div>
  );
};

const AdSummaryCards = ({ summary }: { summary: { activeCampaigns: number } }) => (
  <div className="space-y-4">
    <div className="inline-flex justify-start items-center gap-1.5">
      <Announcement03 className="w-5 h-5 text-tertiary" strokeWidth={2} />
      <div className="text-tertiary text-sm font-medium leading-tight">Ad's summary</div>
    </div>
    <div className="flex rounded-md outline-1 outline-offset-[-1px] outline-border-primary overflow-hidden divide-x divide-border-primary">
      <div className="flex-1 text-center px-4 py-6">
        <div className="text-base font-medium text-primary">{summary.activeCampaigns}</div>
        <div className="text-sm text-tertiary">Active Campaigns</div>
      </div>
    </div>
  </div>
);

const EmptyCampaignsState = ({ creatorId, creatorName }: { creatorId: string; creatorName: string }) => (
  <div>
    <div className="inline-flex justify-start items-center gap-1.5 pb-4">
      <Signal01 className="w-5 h-5 text-tertiary" strokeWidth={2} />
      <div className="text-tertiary text-sm font-medium leading-tight">In 0 Active campaigns</div>
    </div>
    <div className="rounded-lg border border-border-primary px-4 py-6 flex justify-center">
      <a
        href={`/video-explorer?creator=${encodeURIComponent(JSON.stringify([{ id: creatorId, label: creatorName }]))}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-sm font-medium text-primary underline decoration-dotted hover:text-primary/80 transition-colors"
      >
        <VideoRecorder className="w-4 h-4" />
        Browse this creators videos to add to a campaign
      </a>
    </div>
  </div>
);

interface CampaignsTableProps {
  creatorId: string;
  creatorName: string;
  campaigns: {
    advertiserId: string;
    campaignId: string;
    campaignName: string;
  }[];
}

const CampaignsTable = ({ creatorId, creatorName, campaigns }: CampaignsTableProps) => {
  const { mutate: removeFromCampaign, isPending: isRemovingSingle } = useRemoveCreatorFromCampaign(creatorId);
  const { mutate: removeFromAll, isPending: isRemovingAll } = useRemoveCreatorFromAllCampaigns(creatorId);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  const isRemoving = isRemovingSingle || isRemovingAll;
  const allCampaigns = campaigns
    .filter((c, i, arr) => arr.findIndex((a) => a.campaignId === c.campaignId) === i)
    .map((c) => ({ campaignId: c.campaignId, campaignName: c.campaignName }));

  const handleConfirmAction = () => {
    if (!confirmAction) return;

    if (confirmAction.type === "remove-single") {
      removeFromCampaign(
        { campaignId: confirmAction.campaignId },
        {
          onSuccess: () => {
            success("Video successfully removed from campaign.");
            setConfirmAction(null);
          },
          onSettled: () => setConfirmAction(null),
        },
      );
    } else {
      removeFromAll(undefined, {
        onSuccess: () => {
          success("Video successfully removed from all campaigns.");
          setConfirmAction(null);
        },
        onSettled: () => setConfirmAction(null),
      });
    }
  };

  return (
    <div>
      <div className="inline-flex justify-between items-center w-full pb-4">
        <div className="inline-flex justify-start items-center gap-1.5">
          <Signal01 className="w-5 h-5 text-tertiary" strokeWidth={2} />
          <div className="text-tertiary text-sm font-medium leading-tight">
            In {campaigns.length} Active campaign{campaigns.length !== 1 ? "s" : ""}
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="p-1 rounded hover:bg-gray-100 transition-colors cursor-pointer"
              aria-label="More actions"
            >
              <DotsVertical className="w-5 h-5 text-tertiary" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[300px]">
            <DropdownMenuItem asChild className="gap-2 cursor-pointer">
              <a
                href={buildAllCampaignsForCreatorUrl(allCampaigns, creatorId, creatorName)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <VideoRecorder className="w-4 h-4 text-fg-quaternary" />
                <span className="text-secondary text-sm">See creators videos used in all campaigns</span>
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setConfirmAction({ type: "remove-all" })}
              disabled={isRemoving}
              className="gap-2 cursor-pointer"
            >
              <Trash01 className="w-4 h-4 text-fg-quaternary" />
              <span className="text-secondary text-sm">Remove creator from all campaigns</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="rounded-lg border border-border-primary">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-border-primary">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">Campaign</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-tertiary" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border-primary">
            {campaigns.map((campaign) => (
              <tr key={campaign.campaignId}>
                <td className="px-4 py-3 text-sm font-medium max-w-[180px]">
                  <a
                    href={buildTikTokCampaignUrl(campaign.advertiserId, campaign.campaignId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline decoration-dotted decoration-1 underline-offset-2 hover:text-brand-primary transition-colors truncate block"
                    title={campaign.campaignName}
                  >
                    {campaign.campaignName}
                  </a>
                </td>
                <td className="px-4 py-3 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="p-1 rounded hover:bg-gray-100 transition-colors cursor-pointer"
                        aria-label="Campaign actions"
                      >
                        <DotsVertical className="w-4 h-4 text-tertiary" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[300px]">
                      <DropdownMenuItem asChild className="gap-2 cursor-pointer">
                        <a
                          href={buildVideosInCampaignForCreatorUrl(
                            campaign.campaignId,
                            campaign.campaignName,
                            creatorId,
                            creatorName,
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <VideoRecorder className="w-4 h-4 text-fg-quaternary" />
                          <span className="text-secondary text-sm">See creators videos used in Campaign</span>
                        </a>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() =>
                          setConfirmAction({
                            type: "remove-single",
                            campaignId: campaign.campaignId,
                            campaignName: campaign.campaignName,
                          })
                        }
                        disabled={isRemoving}
                        className="gap-2 cursor-pointer"
                      >
                        <Trash01 className="w-4 h-4 text-fg-quaternary" />
                        <span className="text-secondary text-sm">Remove creator from Campaign</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === "remove-all" ? "Remove from all campaigns" : "Remove from campaign"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.type === "remove-all" ? (
                "This will delete all ads linking this creator's videos to their campaigns on TikTok Ads. This action cannot be undone."
              ) : (
                <>
                  This will delete the ads linking this creator's videos to{" "}
                  <span className="font-medium">
                    {confirmAction?.type === "remove-single"
                      ? (confirmAction.campaignName ?? "this campaign")
                      : "this campaign"}
                  </span>{" "}
                  on TikTok Ads. This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAction}
              disabled={isRemoving}
              className="bg-error-solid hover:bg-error-solid/90"
            >
              {isRemoving ? "Removing..." : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const CreatorCampaignsSection = ({ creatorId, creatorName }: { creatorId: string; creatorName: string }) => {
  const { data, isLoading, error } = useCreatorCampaigns(creatorId);
  const { data: connectionStatus, isLoading: isLoadingConnection } = useTikTokAdsConnectionStatus();

  if (isLoading || isLoadingConnection) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingIndicator />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-sm text-error-primary">Failed to load campaign data</p>
      </div>
    );
  }

  const isConnected = connectionStatus?.connected === true;
  const campaigns = data?.campaigns ?? [];
  const summary = data?.summary ?? { activeCampaigns: 0 };

  if (!isConnected) {
    return <NotConnectedState />;
  }

  return (
    <div className="space-y-6">
      <AdSummaryCards summary={summary} />
      {campaigns.length === 0 ? (
        <EmptyCampaignsState creatorId={creatorId} creatorName={creatorName} />
      ) : (
        <CampaignsTable creatorId={creatorId} creatorName={creatorName} campaigns={campaigns} />
      )}
    </div>
  );
};
